import { GripVertical, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import { AppLayout } from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import { getModuleStatus, type Module, MODULES } from "@/data/modules";
import {
  type AgendaRow,
  agendaStorageKey,
  minutesToTime,
  parseDurationToMinutes,
  parseTimeStr,
  seedAgendaRows,
} from "@/lib/agenda";
import { useFacilitatorState, useFacilitatorStateWriter } from "@/lib/api";

import { FacilitatorSaveStatus } from "./save-status";
import { moveRowById, useAgendaDnd } from "./use-agenda-dnd";

// ---------------------------------------------------------------------------
// Year Timeline
// ---------------------------------------------------------------------------

function Timeline() {
  return (
    <div className="mb-12">
      <h2 className="text-foreground mb-6 text-base font-semibold">Year Timeline</h2>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        {MODULES.map((module, idx) => {
          const isCurrent = getModuleStatus(module) === "current";
          const isLast = idx === MODULES.length - 1;
          return (
            <div
              key={module.id}
              className="flex flex-col items-center gap-2 md:flex-row md:items-center"
            >
              <div
                className={`w-full rounded-xl p-4 shadow-sm md:w-44 ${isCurrent ? "bg-primary text-white" : "text-foreground border border-gray-200 bg-white"}`}
              >
                <p
                  className={`mb-1 text-xs font-semibold tracking-wider uppercase ${isCurrent ? "text-white/70" : "text-muted"}`}
                >
                  Module {module.id}
                </p>
                <p
                  className={`text-sm leading-snug font-bold ${isCurrent ? "text-white" : "text-foreground"}`}
                >
                  {module.title}
                </p>
                <p className={`mt-1.5 text-xs ${isCurrent ? "text-white/80" : "text-muted"}`}>
                  {module.location}
                </p>
                <p className={`text-xs ${isCurrent ? "text-white/80" : "text-muted"}`}>
                  {module.month}
                </p>
              </div>
              {!isLast && (
                <>
                  <span className="text-muted hidden text-lg leading-none md:block">→</span>
                  <span className="text-muted block text-lg leading-none md:hidden">↓</span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module planning table (dates / objectives / facilitators / notes)
// ---------------------------------------------------------------------------

function ModulePlanRow({ module }: { module: Module }) {
  const { data: state } = useFacilitatorState();
  const { setKey } = useFacilitatorStateWriter();
  const planKey = (field: string): string => `fac-plan-${field}-${module.id}`;
  const read = (field: string, fallback = ""): string => {
    const k = planKey(field);
    return state && Object.prototype.hasOwnProperty.call(state, k)
      ? (state[k] as string)
      : fallback;
  };

  return (
    <tr className="border-b border-gray-100">
      <td className="px-4 py-3 align-top">
        <p className="text-muted text-xs font-semibold tracking-wide uppercase">M{module.id}</p>
        <p className="text-foreground text-sm font-medium">{module.title}</p>
      </td>
      <td className="px-4 py-3 align-top">
        <p className="text-foreground text-sm">{module.location}</p>
      </td>
      <td className="px-4 py-3 align-top">
        <input
          type="text"
          value={read("dates")}
          placeholder="15–17 April 2025"
          onChange={(e) => setKey(planKey("dates"), e.target.value)}
          className="focus:border-primary text-foreground w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 text-sm placeholder:text-gray-300 focus:outline-none"
        />
      </td>
      <td className="px-4 py-3 align-top">
        <textarea
          value={read("objectives", module.outcome)}
          onChange={(e) => setKey(planKey("objectives"), e.target.value)}
          rows={3}
          className="focus:border-primary text-foreground w-full resize-none rounded border border-gray-200 bg-white px-2.5 py-1.5 text-sm focus:outline-none"
        />
      </td>
      <td className="px-4 py-3 align-top">
        <input
          type="text"
          value={read("facilitators")}
          placeholder="Add facilitators"
          onChange={(e) => setKey(planKey("facilitators"), e.target.value)}
          className="focus:border-primary text-foreground w-full rounded border border-gray-200 bg-white px-2.5 py-1.5 text-sm placeholder:text-gray-300 focus:outline-none"
        />
      </td>
      <td className="px-4 py-3 align-top">
        <textarea
          value={read("notes")}
          placeholder="Add notes"
          onChange={(e) => setKey(planKey("notes"), e.target.value)}
          rows={3}
          className="focus:border-primary text-foreground w-full resize-none rounded border border-gray-200 bg-white px-2.5 py-1.5 text-sm placeholder:text-gray-300 focus:outline-none"
        />
      </td>
    </tr>
  );
}

function ModulePlanTable() {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      <table className="w-full min-w-[800px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            {["Module", "Location", "Dates", "Objectives", "Facilitators", "Notes"].map((h) => (
              <th
                key={h}
                className="text-muted px-4 py-3 text-left text-xs font-semibold tracking-wide uppercase"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MODULES.map((m) => (
            <ModulePlanRow key={m.id} module={m} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full agenda blueprint — 4 editable columns, one per module
// ---------------------------------------------------------------------------

function AgendaColumn({ module }: { module: Module }) {
  const key = agendaStorageKey(module.id);
  const { data: state } = useFacilitatorState();
  const { setKey } = useFacilitatorStateWriter();
  const seed = useMemo(() => seedAgendaRows(module), [module]);
  const rows = (state?.[key] as AgendaRow[] | undefined) ?? seed;

  const save = (next: AgendaRow[]): void => setKey(key, next);

  const { handleProps, rowProps, indicator } = useAgendaDnd((fromId, targetId, pos) =>
    save(moveRowById(rows, fromId, targetId, pos)),
  );

  const updateRow = (id: string, field: keyof AgendaRow, value: string): void => {
    save(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const deleteRow = (id: string): void => save(rows.filter((r) => r.id !== id));

  const addBelow = (id: string): void => {
    const idx = rows.findIndex((r) => r.id === id);
    const ref = rows[idx]!;
    const newRow: AgendaRow = {
      id: crypto.randomUUID(),
      type: "activity",
      day: ref.day,
      number: "",
      name: "",
      duration: "",
      owner: "",
    };
    const next = [...rows];
    next.splice(idx + 1, 0, newRow);
    save(next);
  };

  const addDay = (): void => {
    const maxDay = rows.reduce((m, r) => Math.max(m, r.day), 0);
    const d = maxDay + 1;
    save([
      ...rows,
      {
        id: crypto.randomUUID(),
        type: "day",
        day: d,
        number: "",
        name: `DAY ${d}`,
        duration: "",
        owner: "",
        startTime: "09:00",
      },
      {
        id: crypto.randomUUID(),
        type: "activity",
        day: d,
        number: "",
        name: "",
        duration: "",
        owner: "",
      },
    ]);
  };

  const computedTimes = useMemo(() => {
    const result: Record<string, { start: string; end: string }> = {};
    let cursor = 9 * 60;
    for (const row of rows) {
      if (row.type === "day") {
        cursor = parseTimeStr(row.startTime ?? "09:00");
        result[row.id] = { start: row.startTime ?? "09:00", end: "" };
      } else {
        const start = cursor;
        const dur = parseDurationToMinutes(row.duration);
        const end = start + dur;
        result[row.id] = { start: minutesToTime(start), end: dur > 0 ? minutesToTime(end) : "" };
        if (dur > 0) {
          cursor = end;
        }
      }
    }
    return result;
  }, [rows]);

  const inp = "bg-transparent border-none outline-none w-full text-xs";
  const grid = "grid grid-cols-[16px_1fr_44px_44px_56px_28px]";
  const durInp = "bg-transparent border-none outline-none w-7 text-right text-xs tabular-nums";
  const durationMinsValue = (d: string): string => {
    const mins = parseDurationToMinutes(d);
    return mins > 0 ? String(mins) : "";
  };

  const dropCls = (id: string): string => {
    const ind = indicator(id);
    if (ind === "before") {
      return "border-t-2 border-t-primary";
    }
    if (ind === "after") {
      return "border-b-2 border-b-primary";
    }
    return "";
  };

  return (
    <div className="flex min-w-[260px] flex-1 flex-col">
      {/* Column header */}
      <div className="bg-primary rounded-t-xl px-3 py-2.5 text-center text-white">
        <p className="text-xs font-bold tracking-wider uppercase">M{module.id}</p>
        <p className="mt-0.5 text-xs leading-snug font-semibold opacity-90">{module.title}</p>
      </div>

      {/* Sub-header */}
      <div
        className={`${grid} border-b border-gray-200 bg-gray-50 px-2 py-1.5 text-[10px] font-semibold tracking-wide text-gray-400 uppercase`}
      >
        <span />
        <span>Activity</span>
        <span className="text-center">Start</span>
        <span className="text-center">End</span>
        <span>Duration</span>
        <span />
      </div>

      {/* Rows */}
      <div className="flex-1 rounded-b-xl border border-t-0 border-gray-200 bg-white">
        {rows.map((row) => {
          const times = computedTimes[row.id];

          if (row.type === "day") {
            return (
              <div
                key={row.id}
                {...rowProps(row.id)}
                className={`${grid} items-center bg-[#FF00FF] px-2 py-1.5 ${dropCls(row.id)}`}
              >
                <span
                  {...handleProps(row.id)}
                  title="Drag to reorder"
                  className="inline-flex cursor-grab text-white/60 hover:text-white active:cursor-grabbing"
                >
                  <GripVertical className="h-3 w-3" />
                </span>
                <input
                  className={`${inp} font-bold text-white placeholder:text-white/50`}
                  value={row.name}
                  onChange={(e) => updateRow(row.id, "name", e.target.value)}
                />
                <input
                  className={`${inp} text-center font-bold text-white placeholder:text-white/50`}
                  value={row.startTime ?? "09:00"}
                  onChange={(e) => updateRow(row.id, "startTime", e.target.value)}
                  placeholder="09:00"
                />
                <span />
                <span />
                <button
                  type="button"
                  onClick={() => addBelow(row.id)}
                  className="text-xs text-white/80 hover:text-white"
                >
                  +
                </button>
              </div>
            );
          }

          if (row.type === "lunch") {
            return (
              <div
                key={row.id}
                {...rowProps(row.id)}
                className={`${grid} items-center bg-gray-700 px-2 py-1.5 ${dropCls(row.id)}`}
              >
                <span
                  {...handleProps(row.id)}
                  title="Drag to reorder"
                  className="inline-flex cursor-grab text-white/60 hover:text-white active:cursor-grabbing"
                >
                  <GripVertical className="h-3 w-3" />
                </span>
                <input
                  className={`${inp} font-semibold text-white placeholder:text-white/50`}
                  value={row.name}
                  onChange={(e) => updateRow(row.id, "name", e.target.value)}
                />
                <span className="text-center text-[10px] text-white/70">{times?.start ?? ""}</span>
                <span className="text-center text-[10px] text-white/70">{times?.end ?? ""}</span>
                <div className="flex items-center gap-0.5">
                  <input
                    className={`${durInp} text-white placeholder:text-white/50`}
                    inputMode="numeric"
                    value={durationMinsValue(row.duration)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "");
                      updateRow(row.id, "duration", digits ? `${digits} min` : "");
                    }}
                    placeholder="30"
                  />
                  <span className="text-[10px] text-white/70">min</span>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => addBelow(row.id)}
                    className="text-xs leading-none text-white/70 hover:text-white"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRow(row.id)}
                    className="text-xs leading-none text-white/70 hover:text-red-300"
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          }

          if (row.type === "note") {
            return (
              <div
                key={row.id}
                {...rowProps(row.id)}
                className={`${grid} items-center border-b border-gray-100 bg-amber-50 px-2 py-1.5 ${dropCls(row.id)}`}
              >
                <span
                  {...handleProps(row.id)}
                  title="Drag to reorder"
                  className="inline-flex cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing"
                >
                  <GripVertical className="h-3 w-3" />
                </span>
                <input
                  className={`${inp} col-span-4 text-amber-800`}
                  value={row.name}
                  onChange={(e) => updateRow(row.id, "name", e.target.value)}
                  placeholder="Note…"
                />
                <div className="flex flex-col items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => addBelow(row.id)}
                    className="text-xs leading-none text-gray-400 hover:text-gray-600"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRow(row.id)}
                    className="text-xs leading-none text-gray-400 hover:text-red-500"
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          }

          // activity
          return (
            <div
              key={row.id}
              {...rowProps(row.id)}
              className={`${grid} items-center border-b border-gray-100 bg-white px-2 py-1.5 hover:bg-gray-50 ${dropCls(row.id)}`}
            >
              <span
                {...handleProps(row.id)}
                title="Drag to reorder"
                className="inline-flex cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing"
              >
                <GripVertical className="h-3 w-3" />
              </span>
              <div className="flex min-w-0 items-center gap-1">
                <input
                  className="text-muted w-6 flex-shrink-0 border-none bg-transparent text-xs tabular-nums outline-none"
                  value={row.number}
                  onChange={(e) => updateRow(row.id, "number", e.target.value)}
                  placeholder="–"
                />
                <input
                  className={`${inp} text-foreground`}
                  value={row.name}
                  onChange={(e) => updateRow(row.id, "name", e.target.value)}
                  placeholder="Activity"
                />
              </div>
              <span className="text-muted text-center text-[10px] tabular-nums">
                {times?.start ?? ""}
              </span>
              <span className="text-muted text-center text-[10px] tabular-nums">
                {times?.end ?? ""}
              </span>
              <div className="flex items-center gap-0.5">
                <input
                  className={`${durInp} text-muted`}
                  inputMode="numeric"
                  value={durationMinsValue(row.duration)}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    updateRow(row.id, "duration", digits ? `${digits} min` : "");
                  }}
                  placeholder="30"
                />
                <span className="text-muted text-[10px]">min</span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => addBelow(row.id)}
                  className="text-xs leading-none text-gray-400 hover:text-gray-600"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => deleteRow(row.id)}
                  className="text-xs leading-none text-gray-400 hover:text-red-500"
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}

        {/* Add day */}
        <div className="px-2 py-2">
          <button
            type="button"
            onClick={addDay}
            className="text-primary hover:bg-primary/5 w-full rounded border border-dashed border-current py-1 text-xs font-medium transition-colors"
          >
            + Add day
          </button>
        </div>
      </div>
    </div>
  );
}

function AgendaBlueprint() {
  return (
    <div>
      <div className="mb-4 flex items-end justify-between">
        <div>
          <h2 className="text-foreground text-base font-semibold">Agenda Blueprint</h2>
          <p className="text-muted mt-0.5 text-xs">
            Editable across all modules. Changes sync with each module's Agenda tab.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto pb-4">
        <div className="flex min-w-[900px] gap-3">
          {MODULES.map((m) => (
            <AgendaColumn key={m.id} module={m} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reset — wipe all locally-stored planning data to start fresh for next year
// ---------------------------------------------------------------------------

function ResetPlanningButton() {
  const { resetAll } = useFacilitatorStateWriter();

  const handleClick = async (): Promise<void> => {
    const confirmed = window.confirm(
      "Reset all program planning? This clears the module overview table, the full agenda blueprint, the preparation checklists, and the Miro board links for every module — for everyone. This can't be undone.",
    );
    if (confirmed) {
      await resetAll();
      window.location.reload();
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={() => void handleClick()}>
      Reset for next year
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function FacilitatorPlanningPage() {
  const { isLoading } = useFacilitatorState();

  return (
    <AppLayout>
      <div className="mx-auto max-w-7xl px-6 py-10">
        <Link
          to="/facilitator"
          className="text-muted hover:text-foreground mb-6 inline-block text-sm font-medium transition-colors"
        >
          ← Facilitator home
        </Link>

        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-primary mb-1 text-xs font-semibold tracking-[0.2em] uppercase">
              Tesla Program · 2025
            </p>
            <h1 className="font-heading text-foreground text-3xl font-bold md:text-4xl">
              Program Planning
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <FacilitatorSaveStatus />
            <ResetPlanningButton />
          </div>
        </div>

        {/* Year timeline */}
        <Timeline />

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Module-level planning table */}
            <div className="mb-12">
              <h2 className="text-foreground mb-6 text-base font-semibold">Module Overview</h2>
              <ModulePlanTable />
            </div>

            {/* Full agenda blueprint */}
            <AgendaBlueprint />
          </>
        )}
      </div>
    </AppLayout>
  );
}
